import {Component, OnDestroy, OnInit} from '@angular/core';
import {DownloadService} from "../../../service/download.service";
import {Order} from "../../../model/order";
import {ProductService} from "../../../service/product.service";
import {OrderService} from "../../../service/order.service";
import {ActivatedRoute} from '@angular/router';
import {Property} from "../../../model/property";
import {SearchPipe} from "../../../pipes/search.pipe";
import {NgxIndexedDBService} from "ngx-indexed-db";
import {filter, map, startWith} from "rxjs/operators";
import {Observable, Subject, Subscription} from "rxjs";
import {StorageService} from "../../../service/storage.service";
import {DownloadModalComponent} from "../download-modal/download-modal.component";
import {LoginIdService} from "../../../service/login-id.service";
import {UserGroup} from "../../../model/userGroup";
import {PropertyService} from "../../../service/property.service";
import {HttpHeaders} from "@angular/common/http";
import {MatDialog} from "@angular/material/dialog";
import {faArrowLeft} from "@fortawesome/free-solid-svg-icons";

@Component({
  selector: 'app-oder-details',
  templateUrl: './oder-details.component.html',
  styleUrls: ['./oder-details.component.css']
})
export class OderDetailsComponent implements OnInit, OnDestroy {

  protected readonly faArrowLeft = faArrowLeft;

  products = [];
  order_id: string;
  items: Property [] = [];
  order: Order;
  searchText: string;
  public current: number = 1;
  public total: number;
  public itemsToDisplay: Property[] = [];
  batchStarted: boolean = false;
  indexDBOrderObserver: Observable<Order>;
  currentUserGroup: UserGroup;
  loginIdSubscription: Subscription;
  private stopRequestObservable: Subject<void> = new Subject<void>();


  constructor(private downloadService: DownloadService,
              private productService: ProductService,
              private orderService: OrderService,
              private route: ActivatedRoute,
              private loginIdService: LoginIdService,
              private searchPipe: SearchPipe,
              private dbService: NgxIndexedDBService,
              private storageService: StorageService,
              private propertyService: PropertyService,
              private dialog: MatDialog) {
    this.productService.getProducts().subscribe(products => {
      this.products = products
    });
  }


  async ngOnInit(): Promise<any> {
    await this.loginIdService.loginServiceInitialized$.toPromise();
    this.storageService.listeningForOrderDetailsDownload = false;
    this.indexDBOrderObserver = this.storageService.indexDBChange$.pipe(
        filter(({key}) => key === "orders"),
        map(orders => orders?.value)
    );

    this.indexDBOrderObserver.subscribe((data) => {
      if (this.storageService.listeningForOrderDetailsDownload) {
        this.order = data;
        this.initPropertyStatisticsForOrderProperties();
        this.prepareItems();
        this.storageService.listeningForOrderDetailsDownload = false;
      }
    });

    this.order_id = this.route.snapshot.params.orderid;

    this.dbService.getByKey('orders', "" + this.order_id).subscribe((order: Order) => {
      this.order = order;
      if (!this.order) {
        this.initChildlessOrder();
      } else {
        this.initPropertiesForOrder();
      }
    }, () => {
      this.initChildlessOrder();
    })

    this.loginIdSubscription = this.loginIdService.loginIdChanged$.pipe(startWith(true)).subscribe(() => {
      this.currentUserGroup = this.loginIdService.getCurrentPermissions();
    });
  }

  searchPagination() {
    this.items = this.order.properties;
    if (this.searchText === null || this.searchText === "") {
      this.items = this.order.properties;
      this.items.sort((a, b) => this.sortArray(a, b));
      this.total = Math.ceil(this.order.properties.length / 100);
      this.itemsToDisplay = this.paginate(this.current, 100);
    } else {
      this.items = this.searchPipe.transform(this.items, this.searchText, 'orderDetailsProperty')
      this.items.sort((a, b) => this.sortArray(a, b));
      this.total = Math.ceil(this.items.length / 100);
      this.itemsToDisplay = this.paginate(this.current, 100);
    }
  }


  prepareItems() {
    this.items = this.order.properties;
    this.items.sort((a, b) => this.sortArray(a, b));
    this.total = Math.ceil(this.order.properties.length / 100);
    this.itemsToDisplay = this.paginate(this.current, 100)
  }

  public paginate(current: number, perPage: number): Property[] {
    return [...this.items.slice((current - 1) * perPage).slice(0, perPage)]
  }

  progress(property: Property) {
    if (property.propertyStatistics && property.propertyStatistics.numberOfDocuments && property.propertyStatistics.numberOfDocuments > 0) {
      return property.propertyStatistics.numberOfReceivedDocuments / property.propertyStatistics.numberOfDocuments * 100;
    } else {
      return 0;
    }
  }

  /**
   * Determines if a property contains a document with unseen information by checking the property statistics numberOfUnseenDocumentStatus > 0
   *
   * @param property Property which should be checked for documents with unseen information
   * @return True if the propertyStatistics of a property are present and numberOfUnseenDocumentStatus of the propertyStatistics is > 0 else false
   */
  public containsDocumentWithUnseenInformation(property: Property): boolean {
    return property.propertyStatistics && property.propertyStatistics.numberOfUnseenDocumentStatus > 0
  }

  async downloadNew(property: Property, event: any) {
    event.stopPropagation();
    if (this.amountOfNewDocs(property) > 0) {
      this.storageService.listeningForOrderDetailsDownload = true;
      this.batchStarted = await this.downloadService.downloadForProperty(property, true);
    } else {
      const dialogRef = this.dialog.open(DownloadModalComponent);

      dialogRef.componentInstance.dialogRef = dialogRef;
      dialogRef.componentInstance.property = property;

      dialogRef.afterClosed().subscribe((result) => {
        this.batchStarted = result;
      })
    }
  }

  amountOfNewDocs(property: Property): number {
    if (property.propertyStatistics && property.propertyStatistics.numberOfNotDownloadedDocumentFiles) {
      return property.propertyStatistics.numberOfNotDownloadedDocumentFiles;
    } else {
      return 0;
    }
  }

  public onGoTo(page: number): void {
    this.current = page
    this.itemsToDisplay = this.paginate(this.current, 100)
  }

  public onNext(page: number): void {
    this.current = page + 1
    this.itemsToDisplay = this.paginate(this.current, 100)
  }

  public onPrevious(page: number): void {
    this.current = page - 1
    this.itemsToDisplay = this.paginate(this.current, 100)
  }

  sortArray(a, b) {
    let reA = /[^a-zA-Z]/g;
    let reN = /[^0-9]/g;
    if (a.name == null || a.name.length === 0 || b.name == null || b.name.length === 0)
      return 0;
    let nameA = a.name;
    let nameB = b.name;
    let aA = nameA.replace(reA, "");
    let bA = nameB.replace(reA, "");
    if (aA === bA) {
      let aN = parseInt(nameA.replace(reN, ""), 10);
      let bN = parseInt(nameB.replace(reN, ""), 10);
      return aN === bN ? 0 : aN > bN ? 1 : -1;
    } else {
      return aA > bA ? 1 : -1;
    }

  }


  /**
   * Determines if a property contains an open customer interaction by checking the property statistics numberOfOpenCustomerInteractions > 0
   *
   * @param property Property which should be checked for open customer interactions
   * @return True if the propertyStatistics of a property are present and numberOfOpenCustomerInteractions of the propertyStatistics is > 0 else false
   */
  isActionRequired(property: Property) {
    return property.propertyStatistics && property.propertyStatistics.numberOfOpenCustomerInteractions > 0
  }

  public initChildlessOrder(): void {
    this.orderService.getChildlessOrderById(this.order_id).subscribe((res) => {
      this.order = res;
      this.orderService.getOrderStatistics(this.order_id, {headers: new HttpHeaders({'showing-spinner': ''})}).subscribe((res) => {
        this.order.orderStatistics = res;
      })
      this.initPropertiesForOrder();
    })
  }

  public initPropertiesForOrder(): void {
    this.propertyService.getChildlessPropertiesByOrder(this.order_id).subscribe((properties) => {
      this.order.properties = properties;
      this.initPropertyStatisticsForOrderProperties();
      this.prepareItems();
    })
  }

  canViewProperty(property: Property): boolean {
    const entry = this.currentUserGroup.permissions.get(property.id);
    const canViewProperty = entry ? entry.scopes.includes('VIEW') : false;
    return (
        !this.currentUserGroup ||
        this.currentUserGroup.roles.includes('ADMIN') ||
        canViewProperty
    )
  }

  // can be used for future nested PermissionEntries on properties and documents
  canDownloadDocuments(property: Property): boolean {
    let hasFullOrderDownloadPermission = false;
    let hasPropertyDownloadPermission = false; // used for future nested permissionEntries
    if (this.currentUserGroup && !this.currentUserGroup.roles.includes('ADMIN')) {
      const orderEntry = this.currentUserGroup.permissions.get(this.order_id);
      hasFullOrderDownloadPermission = orderEntry ? orderEntry.scopes.includes('DOWNLOAD') : false;
      const propertyEntry = this.currentUserGroup.permissions.get(property.id);
      hasPropertyDownloadPermission = propertyEntry ? propertyEntry.scopes.includes('DOWNLOAD') : false;
    }
    return (
        !this.currentUserGroup ||
        this.currentUserGroup.roles.includes('ADMIN') ||
        hasFullOrderDownloadPermission ||
        hasPropertyDownloadPermission
    )
  }

  hasDownloadPermissions(): boolean {
    if (this.order) {
      return this.loginIdService.hasPermissionsForOrder(this.order, 'DOWNLOAD');
    }
  }

  hasViewPermissions(): boolean {
    if (this.order) {
      return this.loginIdService.hasPermissionsForOrder(this.order, 'VIEW');
    }
  }

  showToolTip() {
    return this.hasDownloadPermissions() ? '' : 'Sie haben nicht die erforderlichen Berechtigungen, um diese Dokumente herunterzuladen!';
  }

  ngOnDestroy(): void {
    this.cancelPropertyStatisticRequests();
    if (this.loginIdSubscription) {
      this.loginIdSubscription.unsubscribe();
    }
  }

  /**
   * Determines if a specific property has statistics
   *
   * @param property Property that should be checked
   * @return true if the property has statistics else false
   */
  public hasPropertyStatistics(property: Property): boolean {
    return !!(property.propertyStatistics);
  }


  /**
   * Get the property statistics for each property of this.order and set the correct statistics object for each property
   */
  public initPropertyStatisticsForOrderProperties(): void {
    this.propertyService.getPropertyStatisticsForOrderProperties(this.order.orderid, {headers: new HttpHeaders({'showing-spinner': ''})}).subscribe((statistics) => {
      for (const propertyStatistics of statistics) {
        const propertyIndex = this.order.properties.findIndex(property => property.id === propertyStatistics.propertyId)
        this.order.properties[propertyIndex].propertyStatistics = propertyStatistics;
      }
      this.storageService.setIndexedDbItem({key: "orders", value: this.order});
    })
  }

  /**
   * Cancel all running requests for getting property statistics
   */
  public cancelPropertyStatisticRequests(): void {
    this.stopRequestObservable.next();
  }

}
